import React from 'react';
import { cn } from '@/lib/utils';
import { InlineLoader } from '@/components/ui/progressive-loading';
import { useTranslation } from '@/hooks/useTranslation';

interface FullScreenLoaderProps {
  variant?: 'basic' | 'with-logo' | 'with-progress' | 'with-text';
  text?: string;
  progress?: number;
  className?: string;
}

/**
 * Professional full-screen loading overlay
 * Replaces all hard-coded Suspense fallbacks with consistent design
 */
export function FullScreenLoader({ 
  variant = 'basic', 
  text, 
  progress,
  className 
}: FullScreenLoaderProps) {
  const { t } = useTranslation('common');
  
  const loadingText = text || t('loading_default');
  
  return (
    <div className={cn(
      "fixed inset-0 z-50 flex items-center justify-center",
      "bg-background/80 backdrop-blur-sm",
      className
    )}>
      <div className="flex flex-col items-center space-y-4 p-8">
        {/* Main loader */}
        <div className="relative">
          <InlineLoader 
            size="lg" 
            variant="spinner" 
            className="text-primary"
          />
          
          {/* Progress ring overlay for with-progress variant */}
          {variant === 'with-progress' && typeof progress === 'number' && (
            <div className="absolute inset-0 flex items-center justify-center">
              <svg className="w-8 h-8 -rotate-90" viewBox="0 0 32 32">
                <circle
                  cx="16"
                  cy="16"
                  r="14"
                  stroke="currentColor"
                  strokeWidth="2"
                  fill="none"
                  className="text-muted"
                />
                <circle
                  cx="16"
                  cy="16"
                  r="14"
                  stroke="hsl(var(--primary))"
                  strokeWidth="2"
                  fill="none"
                  strokeDasharray={87.96}
                  strokeDashoffset={87.96 - (87.96 * progress) / 100}
                  className="transition-all duration-300"
                />
              </svg>
            </div>
          )}
        </div>

        {/* Logo for with-logo variant */}
        {variant === 'with-logo' && (
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
              <span className="text-primary-foreground font-bold text-sm">T</span>
            </div>
            <span className="text-lg font-medium text-foreground">TalentHero</span>
          </div>
        )}

        {/* Loading text */}
        {(variant === 'with-text' || variant === 'with-progress' || text) && (
          <div className="text-center space-y-1">
            <p className="text-sm font-medium text-foreground">{loadingText}</p>
            {variant === 'with-progress' && typeof progress === 'number' && (
              <p className="text-xs text-muted-foreground">
                {t('loading_with_progress', { progress })}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}